import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PipelineMasterSteps } from 'global-stores';
import { CurrentStepState } from '../../../store';
import './delivery-left.scss';

/**
 * The Delivery Method rail.
 *
 * IT HAS TO READ AS THREE STEPS. It was a flat list of three icons and labels
 * with the active one marked by a 4px bar on the FAR RIGHT EDGE of the rail —
 * nowhere near the row it belonged to. Nothing said how many steps there were,
 * which order they ran in, or how far through them you were, so it read as a
 * navigation menu that happened to have three entries.
 *
 * So: numbered marks joined by a spine, the way a stepper is drawn everywhere
 * else in the product. The number is the thing that says "three of these, in
 * this order"; a step you have already passed shows a tick instead, which is
 * what says "and you are here".
 *
 * The icons are gone deliberately. A mark can carry a number or a glyph, not
 * both, and the sequence is the fact worth carrying — three round icons said
 * nothing about order. The labels are unchanged and still name each step.
 */
export const DeliverLeftView = () => {
	const masterSteps = useRecoilValue(PipelineMasterSteps);
	const [renderedObj, setRenderedObj] = useRecoilState(CurrentStepState);

	const handleSelectStep = useCallback(
		(act: any) => {
			setRenderedObj(act);
		},
		[setRenderedObj]
	);

	const deliveryActions = useMemo(
		() =>
			masterSteps.find(step => step.key === 'deliveryMethod')?.actions ?? [],
		[masterSteps]
	);

	/** Which step is open, so the ones before it can be drawn as passed. */
	const activeIndex = useMemo(
		() =>
			deliveryActions.findIndex(
				(a: any) => a?.label === renderedObj?.label
			),
		[deliveryActions, renderedObj]
	);

	return (
		<div className="OnboardingLeftNav--container">
			<div className="DeliveryLeftView__container">
				<div className="DeliveryLeftView__head">
					<span className="DeliveryLeftView__head-title">Delivery method</span>
					<span className="DeliveryLeftView__head-sub">
						{/* the count is read off the list rather than written as "3", so
							it cannot go stale if a step is ever added or removed */}
						Step {activeIndex < 0 ? 1 : activeIndex + 1} of{' '}
						{deliveryActions.length}
					</span>
				</div>

				<div className="DeliveryLeftView__steps">
					{deliveryActions.map((act: any, i: number) => {
						const isActive = act?.label === renderedObj?.label;
						const isDone = activeIndex > -1 && i < activeIndex;
						return (
							<button
								type="button"
								key={act.label}
								className={`DeliveryLeftView__box${
									isActive ? ' is-active' : ''
								}${isDone ? ' is-done' : ''}`}
								onClick={() => handleSelectStep(act)}
								aria-current={isActive ? 'step' : undefined}
							>
								<span className="DeliveryLeftView__mark" aria-hidden="true">
									{isDone ? (
										/* inline SVG, not an icon font: remixicon loads from a
										   CDN that is blocked here, and a missing glyph renders
										   as an empty box indistinguishable from no mark */
										<svg viewBox="0 0 16 16" width="11" height="11">
											<path
												d="M2.5 8.5l3.2 3.2 7-7"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.2"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									) : (
										i + 1
									)}
								</span>
								<span className="DeliveryLeftView__label">{act.label}</span>
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
};
